import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ConfirmDialog } from '../ui/ConfirmDialog';
import { ButtonSpinner } from '../ui/Loader';
import {
  useGetWebhookUrlQuery,
  useSetWebhookUrlMutation,
  useDeleteWebhookUrlMutation,
} from '../../store/api/userApi';

export function WebhookSettings() {
  const { t } = useTranslation();
  const { data: webhookData, refetch } = useGetWebhookUrlQuery();
  const [setWebhook, { isLoading: isSetting }] = useSetWebhookUrlMutation();
  const [deleteWebhook, { isLoading: isDeleting }] = useDeleteWebhookUrlMutation();

  const [webhookUrl, setWebhookUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const currentWebhookUrl = webhookData?.data.webhookUrl;

  useEffect(() => {
    if (currentWebhookUrl) {
      setWebhookUrl(currentWebhookUrl);
    } else {
      setWebhookUrl('');
    }
  }, [currentWebhookUrl]);

  const validateUrl = (url: string): boolean => {
    if (!url.trim()) {
      setUrlError(t('profile.webhook.errorRequired'));
      return false;
    }

    try {
      const urlObj = new URL(url);
      if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
        setUrlError(t('profile.webhook.errorInvalidUrl'));
        return false;
      }
      setUrlError(null);
      return true;
    } catch {
      setUrlError(t('profile.webhook.errorInvalidUrl'));
      return false;
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setWebhookUrl(value);
    if (value.trim()) {
      validateUrl(value);
    } else {
      setUrlError(null);
    }
  };

  const handleSetWebhook = async (e: React.FormEvent) => {
    e.preventDefault();
    setUrlError(null);

    if (!validateUrl(webhookUrl)) {
      return;
    }

    try {
      await setWebhook({ webhookUrl: webhookUrl.trim() }).unwrap();
      toast.success(
        currentWebhookUrl
          ? t('profile.webhook.successUpdate')
          : t('profile.webhook.successSet')
      );
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteWebhook().unwrap();
      toast.success(t('profile.webhook.successDelete'));
      setWebhookUrl('');
      setShowDeleteDialog(false);
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleCopyUrl = () => {
    if (currentWebhookUrl) {
      navigator.clipboard.writeText(currentWebhookUrl);
      toast.success(t('common.copied'));
    }
  };

  return (
    <div className="bg-neutral-dark/50 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-4 sm:p-6 md:p-8 animate-scale-in">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-heading font-bold text-brand-accent">
          {t('profile.webhook.title')}
        </h2>
        {currentWebhookUrl && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm text-green-500 font-body">
              {t('common.active')}
            </span>
          </div>
        )}
      </div>

      <p className="text-neutral-medium mb-6 font-body">
        {t('profile.webhook.description')}
      </p>

      {/* Current Webhook URL */}
      {currentWebhookUrl && (
        <div className="mb-6 p-4 bg-neutral-dark/30 rounded-lg border border-neutral-dark">
          <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
            {t('profile.webhook.currentUrl')}
          </label>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <input
              type="text"
              value={currentWebhookUrl}
              readOnly
              className="flex-1 min-w-0 px-4 py-2 bg-neutral-dark/50 border border-neutral-dark rounded-lg text-neutral-light font-body text-sm truncate"
            />
            <button
              onClick={handleCopyUrl}
              className="px-4 py-2 bg-brand-accent/20 text-brand-accent font-heading font-semibold rounded-lg hover:bg-brand-accent/30 transition-all duration-300 flex items-center justify-center gap-2"
              title={t('common.copy')}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {!currentWebhookUrl && (
        <div className="mb-6 p-4 bg-neutral-dark/30 rounded-lg border border-neutral-dark">
          <p className="text-neutral-medium font-body">
            {t('profile.webhook.notSet')}
          </p>
        </div>
      )}

      {/* Webhook URL Form */}
      <form onSubmit={handleSetWebhook} className="space-y-4">
        <div>
          <label
            htmlFor="webhookUrl"
            className="block text-sm font-medium text-neutral-light mb-2 font-body"
          >
            {t('profile.webhook.urlLabel')}
          </label>
          <input
            type="text"
            id="webhookUrl"
            value={webhookUrl}
            onChange={handleUrlChange}
            onBlur={() => webhookUrl.trim() && validateUrl(webhookUrl)}
            className={`w-full px-4 py-3 bg-neutral-dark/50 backdrop-blur-sm border rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-300 font-body ${
              urlError ? 'border-danger' : 'border-neutral-dark'
            }`}
            placeholder={t('profile.webhook.urlPlaceholder')}
          />
          {urlError && (
            <p className="mt-2 text-sm text-danger font-body">{urlError}</p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={isSetting || !!urlError || !webhookUrl.trim()}
            className="flex-1 px-4 sm:px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            {isSetting ? (
              <>
                <ButtonSpinner />
                <span>{t('common.loading')}</span>
              </>
            ) : currentWebhookUrl ? (
              t('profile.webhook.updateButton')
            ) : (
              t('profile.webhook.setButton')
            )}
          </button>
          {currentWebhookUrl && (
            <button
              type="button"
              onClick={() => setShowDeleteDialog(true)}
              disabled={isDeleting || isSetting}
              className="flex-1 px-4 sm:px-6 py-3 bg-danger/20 text-danger font-heading font-semibold rounded-lg hover:bg-danger/30 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm sm:text-base"
            >
              {t('profile.webhook.deleteButton')}
            </button>
          )}
        </div>
      </form>

      {/* Info Section */}
      <div className="mt-6 p-4 bg-brand-accent/10 rounded-lg border border-brand-accent/20">
        <h3 className="text-lg font-heading font-semibold text-brand-accent mb-2">
          {t('profile.webhook.infoTitle')}
        </h3>
        <p className="text-sm text-neutral-medium mb-3 font-body">
          {t('profile.webhook.infoDescription')}
        </p>
        <div className="mt-3">
          <h4 className="text-sm font-heading font-semibold text-neutral-light mb-1">
            {t('profile.webhook.infoPayloadTitle')}
          </h4>
          <p className="text-xs text-neutral-medium font-body">
            {t('profile.webhook.infoFields')}
          </p>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title={t('profile.webhook.deleteButton')}
        message={t('profile.webhook.deleteConfirm')}
        confirmText={t('profile.webhook.deleteButton')}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}

